import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

interface AcademyProfileProps {
  academyId: string;
  academyInfo: any;
  onUpdate: () => void;
}

const AcademyProfile: React.FC<AcademyProfileProps> = ({ academyId, academyInfo, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!academyId) {
      setError('Academy ID is required');
    }
  }, [academyId]);
  
  const [formData, setFormData] = useState({
    name: '',
    academy_type: 'sports_academy',
    description: '',
    country: '',
    city: '',
    address: '',
    contact_email: '',
    contact_phone: '',
  });

  useEffect(() => {
    if (academyInfo) {
      setFormData({
        name: academyInfo.name || '',
        academy_type: academyInfo.academy_type || 'sports_academy',
        description: academyInfo.description || '',
        country: academyInfo.country || '',
        city: academyInfo.city || '',
        address: academyInfo.address || '',
        contact_email: academyInfo.contact_email || '',
        contact_phone: academyInfo.contact_phone || '',
      });
    }
  }, [academyInfo]);

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Academy name is required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/academies/${academyId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          academy_type: formData.academy_type,
          description: formData.description || null,
          country: formData.country || null,
          city: formData.city || null,
          address: formData.address || null,
          contact_email: formData.contact_email || null,
          contact_phone: formData.contact_phone || null,
        }),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Academy profile updated successfully');
        onUpdate();
      } else {
        toast.error(result.message || 'Failed to update academy profile');
        setError(result.message || 'Failed to update academy profile');
      }
    } catch (err: any) {
      console.error('Error updating academy profile:', err);
      toast.error('Failed to update academy profile');
      setError('Failed to update academy profile');
    } finally {
      setLoading(false);
    }
  };

  if (!academyInfo) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Academy Profile
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {academyInfo?.academy_id_code && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Academy ID
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {academyInfo.academy_id_code}
                  </Typography>
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Academy Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Academy Type *</InputLabel>
                <Select
                  value={formData.academy_type}
                  label="Academy Type *"
                  onChange={(e) => setFormData({ ...formData, academy_type: e.target.value })}
                >
                  <MenuItem value="regular_school">Regular School (Education and Academy)</MenuItem>
                  <MenuItem value="sports_academy">Sports Academy (Only Sports)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
                sx={{ minWidth: 120 }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AcademyProfile;

